import { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/useToast';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useProjectRepo } from '@/hooks/useProjectRepo';
import { useProjectDeploySettings } from '@/hooks/useProjectDeploySettings';
import { Share2, X as XIcon, MessageSquare, ExternalLink } from 'lucide-react';
import { nip19 } from 'nostr-tools';

interface ShareProjectDialogProps {
  projectId: string;
  projectName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShareProjectDialog({
  projectId,
  projectName,
  open,
  onOpenChange,
}: ShareProjectDialogProps) {
  const { user } = useCurrentUser();
  const { toast } = useToast();
  const { mutate: publishEvent, isPending: isPublishing } = useNostrPublish();
  const { data: repoInfo } = useProjectRepo(projectId);
  const { settings: deploySettings } = useProjectDeploySettings(projectId);

  // Generate project URL based on available data
  const projectUrl = useMemo(() => {
    // Priority 1: Last deployment URL
    if (deploySettings.lastDeployUrl) {
      return deploySettings.lastDeployUrl;
    }

    // Priority 2: Nostr repository naddr
    if (repoInfo?.naddr) {
      return `https://afterhours.diy/${repoInfo.naddr}`;
    }

    // Priority 3: Fallback to user's npub + project ID
    if (user?.pubkey) {
      const npub = nip19.npubEncode(user.pubkey);
      return `https://afterhours.diy/${npub}/${projectId}`;
    }

    // No URL available
    return null;
  }, [deploySettings.lastDeployUrl, repoInfo?.naddr, user?.pubkey, projectId]);

  const defaultMessage = useMemo(
    () => `Check out my project: ${projectName} on AfterHours!${projectUrl ? `\n\n${projectUrl}` : ''}`,
    [projectName, projectUrl]
  );
  const [message, setMessage] = useState(defaultMessage);

  // Reset message when defaultMessage changes or when dialog opens
  useEffect(() => {
    setMessage(defaultMessage);
  }, [defaultMessage]);

  // Reset message when dialog opens
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setMessage(defaultMessage);
    }
    onOpenChange(newOpen);
  };

  const handleShareOnNostr = () => {
    if (!user) {
      toast({
        title: 'Not logged in',
        description: 'Please log in to share on Nostr',
        variant: 'destructive',
      });
      return;
    }

    publishEvent(
      {
        kind: 1,
        content: message,
        tags: [],
      },
      {
        onSuccess: () => {
          toast({
            title: 'Shared on Nostr!',
            description: 'Your project has been shared successfully.',
          });
          onOpenChange(false);
        },
        onError: (error) => {
          toast({
            title: 'Failed to share',
            description: error instanceof Error ? error.message : 'Unknown error',
            variant: 'destructive',
          });
        },
      }
    );
  };

  const handleShareOnX = () => {
    // Encode the message for URL
    const encodedMessage = encodeURIComponent(message);
    const xUrl = `https://twitter.com/intent/tweet?text=${encodedMessage}`;
    window.open(xUrl, '_blank', 'noopener,noreferrer');
    onOpenChange(false);
  };

  const handleOpenLivePreview = () => {
    if (deploySettings.lastDeployUrl) {
      window.open(deploySettings.lastDeployUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share Project
          </DialogTitle>
          <DialogDescription>
            Share your project with the world
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Live preview link if available */}
          {deploySettings.lastDeployUrl && (
            <div className="rounded-lg border bg-muted/50 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium mb-1">Live Preview</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {deploySettings.lastDeployUrl}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenLivePreview}
                  className="flex-shrink-0"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open
                </Button>
              </div>
            </div>
          )}

          {/* Message composer */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Message</label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write a message about your project..."
              className="min-h-[120px] resize-none"
              disabled={isPublishing}
            />
            <p className="text-xs text-muted-foreground">
              {message.length} characters
            </p>
          </div>

          {/* Share buttons */}
          <div className="flex gap-3">
            <Button
              onClick={handleShareOnNostr}
              disabled={!user || !message.trim() || isPublishing}
              className="flex-1 gap-2"
            >
              <MessageSquare className="h-4 w-4" />
              {isPublishing ? 'Sharing...' : 'Share on Nostr'}
            </Button>
            <Button
              onClick={handleShareOnX}
              disabled={!message.trim() || isPublishing}
              variant="outline"
              className="flex-1 gap-2"
            >
              <XIcon className="h-4 w-4" />
              Share on X
            </Button>
          </div>

          {!user && (
            <p className="text-xs text-muted-foreground text-center">
              Log in to share directly on Nostr
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
